import React, { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Eye, EyeOff, Lock, CheckCircle2 } from "lucide-react";

export default function AdminReset() {
  const { token } = useParams();
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (pw !== pw2) { setError("Passwords do not match."); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset", { token, new_password: pw });
      setDone(true);
      toast.success("Password updated");
      setTimeout(() => nav("/admin/login"), 1600);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Could not reset password";
      setError(detail);
      toast.error(detail);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-[--sanctum-bg]">
      <div className="w-full max-w-sm bg-white rounded-2xl border border-[--sanctum-border] p-8 shadow-[0_20px_60px_rgba(0,0,0,0.05)]">
        <h1 className="font-display text-3xl mb-1">Choose a new password</h1>
        <p className="text-sm text-neutral-500 mb-6">At least 8 characters. Case-sensitive.</p>

        {done ? (
          <div data-testid="reset-done-panel" className="text-center py-4">
            <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto mb-3" strokeWidth={1.2} />
            <p className="font-display text-2xl">Password updated.</p>
            <p className="text-sm text-neutral-500 mt-2">Redirecting you to sign in…</p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="eyebrow block mb-1.5">New password</label>
              <div className="relative">
                <Lock size={14} strokeWidth={1.5} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
                <input
                  data-testid="reset-password"
                  type={showPw ? "text" : "password"}
                  required autoFocus autoComplete="new-password"
                  value={pw} onChange={(e) => setPw(e.target.value)}
                  className="w-full h-11 pl-9 pr-10 rounded-lg border border-[--sanctum-border] bg-white focus:outline-none focus:border-neutral-400"
                />
                <button
                  type="button"
                  data-testid="reset-toggle"
                  onClick={() => setShowPw((v) => !v)}
                  aria-label="Show password"
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-neutral-500 hover:text-neutral-900"
                >
                  {showPw ? <EyeOff size={14} strokeWidth={1.5} /> : <Eye size={14} strokeWidth={1.5} />}
                </button>
              </div>
            </div>
            <div>
              <label className="eyebrow block mb-1.5">Confirm password</label>
              <input
                data-testid="reset-password-confirm"
                type={showPw ? "text" : "password"}
                required autoComplete="new-password"
                value={pw2} onChange={(e) => setPw2(e.target.value)}
                className="w-full h-11 px-3 rounded-lg border border-[--sanctum-border] bg-white focus:outline-none focus:border-neutral-400"
              />
            </div>

            {error && (
              <div
                data-testid="reset-error"
                className="text-xs text-red-700 bg-red-50 border border-red-100 rounded-lg px-3 py-2"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              data-testid="reset-submit"
              disabled={loading}
              className="btn-pill btn-primary w-full mt-4"
            >
              {loading ? "Updating…" : "Update password"}
            </button>
            <div className="text-center pt-2">
              <Link to="/admin/login" className="text-xs text-neutral-500 hover:text-neutral-900 underline underline-offset-4">
                Back to sign in
              </Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
